import { useEffect, type RefObject } from 'react';

const FOCUSABLE =
  'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

function focusables(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE));
}

// Only the most recently activated trap handles Tab (a drawer opened over a modal, …).
const traps: HTMLElement[] = [];

/**
 * While `active`: moves focus into `ref` (a [data-autofocus] element if there is one, else the container
 * itself, which shows no ring, as in the design), keeps Tab and Shift+Tab inside, and on deactivation
 * returns focus to where it was.
 */
export function useFocusTrap(ref: RefObject<HTMLElement | null>, active: boolean): void {
  useEffect(() => {
    if (!active) return;
    const root = ref.current;
    if (!root) return;
    const previous = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    traps.push(root);
    const first = root.querySelector<HTMLElement>('[data-autofocus]') ?? root;
    first.focus();

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab' || traps[traps.length - 1] !== root) return;
      const items = focusables(root);
      if (!items.length) {
        e.preventDefault();
        root.focus();
        return;
      }
      const firstItem = items[0];
      const lastItem = items[items.length - 1];
      const current = document.activeElement;
      if (e.shiftKey && (current === firstItem || !root.contains(current) || current === root)) {
        e.preventDefault();
        lastItem?.focus();
      } else if (!e.shiftKey && (current === lastItem || !root.contains(current))) {
        e.preventDefault();
        firstItem?.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      const i = traps.lastIndexOf(root);
      if (i >= 0) traps.splice(i, 1);
      if (previous && document.contains(previous)) previous.focus();
    };
  }, [ref, active]);
}
